import React, {
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import client from '@api/client';
import ConversationsContext, {
  ConversationSummary,
  ConversationsContextType,
} from './ConversationsContextStore';

const PAGE_SIZE = 50;

interface ConversationsPage {
  conversations: ConversationSummary[];
  totalElements: number;
  hasMore: boolean;
}

/** Mirrors the control-plane `ConversationListResponse` record. */
interface ConversationsResponse {
  conversations: ConversationSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

type ConversationsInfiniteData = InfiniteData<ConversationsPage, number>;

// API functions
const fetchConversationsApi = async (page: number): Promise<ConversationsPage> => {
  const response = await client.get<ConversationsResponse>('/api/v1/conversations', {
    params: { page, size: PAGE_SIZE },
  });
  const { conversations = [], totalElements } = response.data;
  return {
    conversations,
    totalElements,
    hasMore: (page + 1) * PAGE_SIZE < totalElements,
  };
};

const renameConversationApi = async (conversationId: string, title: string): Promise<void> => {
  await client.patch(`/api/v1/conversations/${conversationId}`, { title });
};

const deleteConversationApi = async (conversationId: string): Promise<void> => {
  await client.delete(`/api/v1/conversations/${conversationId}`);
};

// Query keys
const conversationsQueryKey = ['conversations'] as const;

// Cache update helpers — extracted to avoid deeply nested functions
const updatePageConversations = (
  old: ConversationsInfiniteData | undefined,
  updater: (conversations: ConversationSummary[]) => ConversationSummary[],
): ConversationsInfiniteData | undefined => {
  if (!old) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      conversations: updater(page.conversations),
    })),
  };
};

interface ConversationsProviderProps {
  children: ReactNode;
}

export const ConversationsProvider: React.FC<ConversationsProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  // Fetch conversations with infinite query
  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: conversationsQueryKey,
    queryFn: ({ pageParam }) => fetchConversationsApi(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
    staleTime: 30000,
    retry: (failureCount, error) => {
      if (error && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 401 || status === 403) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });

  // Flatten all pages into a single list
  const conversations = useMemo(
    () => data?.pages.flatMap((page) => page.conversations) ?? [],
    [data],
  );

  /** Apply an updater to the cached conversations list (flattened setQueryData + updatePageConversations). */
  const updateConversationsCache = useCallback(
    (updater: (conversations: ConversationSummary[]) => ConversationSummary[]) => {
      queryClient.setQueryData<ConversationsInfiniteData>(conversationsQueryKey, (old) =>
        updatePageConversations(old, updater),
      );
    },
    [queryClient],
  );

  // Rename mutation
  const renameMutation = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      renameConversationApi(conversationId, title),
    onSuccess: (_, { conversationId, title }) => {
      updateConversationsCache((convs) =>
        convs.map((c) => (c.conversationId === conversationId ? { ...c, title } : c)),
      );
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteConversationApi,
    onSuccess: (_, conversationId) => {
      updateConversationsCache((convs) =>
        convs.filter((c) => c.conversationId !== conversationId),
      );
    },
  });

  const fetchConversations = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleFetchNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const addConversation = useCallback(
    (conversation: ConversationSummary) => {
      queryClient.setQueryData<ConversationsInfiniteData>(conversationsQueryKey, (old) => {
        if (!old) return old;
        const firstPage = old.pages[0];
        return {
          ...old,
          pages: [
            { ...firstPage, conversations: [...firstPage.conversations, conversation] },
            ...old.pages.slice(1),
          ],
        };
      });
    },
    [queryClient],
  );

  const prependConversation = useCallback(
    (conversation: ConversationSummary) => {
      const excludeCurrent = (convs: ConversationSummary[]) =>
        convs.filter((c) => c.conversationId !== conversation.conversationId);

      queryClient.setQueryData<ConversationsInfiniteData>(conversationsQueryKey, (old) => {
        if (!old) {
          // Conversations query hasn't loaded yet (e.g. SSE fires before the
          // initial GET /conversations response arrives).  Seed the cache so the
          // conversation appears immediately; the eventual server response will
          // already include this conversation since it is committed to the DB
          // before the SSE started event is emitted.
          return {
            pages: [{ conversations: [conversation], totalElements: 1, hasMore: false }],
            pageParams: [0],
          };
        }

        const filtered = updatePageConversations(old, excludeCurrent);
        if (!filtered) return old;

        // Prepend to the first page
        const firstPage = filtered.pages[0];
        return {
          ...filtered,
          pages: [
            { ...firstPage, conversations: [conversation, ...firstPage.conversations] },
            ...filtered.pages.slice(1),
          ],
        };
      });
    },
    [queryClient],
  );

  const updateConversation = useCallback(
    (conversationId: string, updates: Partial<ConversationSummary>) => {
      updateConversationsCache((convs) =>
        convs.map((conv) =>
          conv.conversationId === conversationId ? { ...conv, ...updates } : conv,
        ),
      );
    },
    [updateConversationsCache],
  );

  const renameConversation = useCallback(
    async (conversationId: string, title: string): Promise<void> => {
      await renameMutation.mutateAsync({ conversationId, title });
    },
    [renameMutation],
  );

  const deleteConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      await deleteMutation.mutateAsync(conversationId);
    },
    [deleteMutation],
  );

  const getErrorMessage = (err: unknown): string | null => {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (err !== null && err !== undefined) {
      try {
        return typeof err === 'object' ? JSON.stringify(err) : String(err);
      } catch {
        return 'An unknown error occurred';
      }
    }
    return null;
  };

  const value: ConversationsContextType = useMemo(
    () => ({
      conversations,
      isLoading,
      error: getErrorMessage(error),
      hasNextPage: hasNextPage ?? false,
      isFetchingNextPage,
      fetchNextPage: handleFetchNextPage,
      fetchConversations,
      addConversation,
      updateConversation,
      renameConversation,
      deleteConversation,
      prependConversation,
    }),
    [
      conversations,
      isLoading,
      error,
      hasNextPage,
      isFetchingNextPage,
      handleFetchNextPage,
      fetchConversations,
      addConversation,
      updateConversation,
      renameConversation,
      deleteConversation,
      prependConversation,
    ],
  );

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
};

export default ConversationsContext;
